// src/pages/SanaNote/EditSanaNote.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import SanaNoteForm from "./SanaNoteForm";
import {
    fetchSanaNote,
    updateSanaNote,
} from "../../../store/slices/sanaNotesSlice";

const EditSanaNote = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentSanaNote, isLoading, error } = useSelector(
        (state) => state.sanaNotes
    );

    const [formData, setFormData] = useState(null);
    const [errors, setErrors] = useState({});

    // ── FETCH DATA ────────────────────────────────────────────────
    useEffect(() => {
        dispatch(fetchSanaNote(id));
    }, [id, dispatch]);

    // ── SYNC REDUX → LOCAL FORM DATA ─────────────────────────────
    useEffect(() => {
        if (currentSanaNote) {
            const mapped = {
                patientId: patientId || currentSanaNote.patient_id || "",
                caseId: caseId || currentSanaNote.case_id || "",
                clientName: currentSanaNote.client_name || "",
                progressNotes: (currentSanaNote.progress_rows || []).map((note) => ({
                    id: note.id,
                    dateTime: note.date_time || "",
                    pulse: note.pulse || "",
                    bp: note.bp || "",
                    rr: note.rr || "",
                    temp: note.temp || "",
                    weight: note.weight || "",
                    bsf: note.bsf || "",
                    bsr: note.bsr || "",
                    height: note.height || "",
                    bmi: note.bmi || "",
                    abdCirc: note.abd_circ || "",
                    chestCirc: note.chest_circ || "",
                    hba1c: note.hba1c || "",
                    urea: note.urea || "",
                    crt: note.crt || "",
                    lipidPro: note.lipid_pro || "",
                    physicianNote: note.physician_note || "",
                    nurseName: note.nurse_name || "",
                })),
                documentName: currentSanaNote.document_name || "Progress Notes (Physician)",
                effectiveDate: currentSanaNote.effective_date || "",
                approvedBy: currentSanaNote.approved_by || "",
                approvedDate: currentSanaNote.approved_date || "",
                nextRevisionBy: currentSanaNote.next_revision_by || "",
                nextRevisionDate: currentSanaNote.next_revision_date || "",
            };
            setFormData(mapped);
        }
    }, [currentSanaNote]);

    // ── HANDLE INPUT CHANGE ───────────────────────────────────────
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // ── VALIDATION ────────────────────────────────────────────────
    const validateForm = () => {
        const newErrors = {};
        if (!formData?.clientName) newErrors.clientName = "Client name is required.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ── HANDLE SUBMIT (UPDATE) ───────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill in all required fields.",
                icon: "warning",
            });
            return;
        }

        const hasFilledRow = formData.progressNotes.some((row) => {
            return (
                row.dateTime ||
                row.pulse ||
                row.bp ||
                row.rr ||
                row.temp ||
                row.weight ||
                row.bsf ||
                row.bsr ||
                row.physicianNote ||
                row.nurseName ||
                row.height ||
                row.bmi ||
                row.abdCirc ||
                row.chestCirc ||
                row.hba1c ||
                row.urea ||
                row.crt ||
                row.lipidPro
            );
        });

        if (!hasFilledRow) {
            // setErrors({ progressNotes: "Please fill at least one progress note row." });
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill at least one progress note row before submitting.",
                icon: "warning",
            });
            return;
        }

        const payload = {
            patient_id: formData.patientId || patientId,
            case_id: formData.caseId || caseId,
            client_name: formData.clientName,
            progress_notes: formData.progressNotes.map((note) => ({
                id: note.id,
                date_time: note.dateTime,
                pulse: note.pulse || null,
                bp: note.bp || null,
                rr: note.rr || null,
                temp: note.temp || null,
                weight: note.weight || null,
                bsf: note.bsf || null,
                bsr: note.bsr || null,
                height: note.height || null,
                bmi: note.bmi || null,
                abd_circ: note.abdCirc || null,
                chest_circ: note.chestCirc || null,
                hba1c: note.hba1c || null,
                urea: note.urea || null,
                crt: note.crt || null,
                lipid_pro: note.lipidPro || null,
                physician_note: note.physicianNote,
                nurse_name: note.nurseName,
            })),
            document_name: formData.documentName,
            effective_date: formData.effectiveDate || null,
            approved_by: formData.approvedBy,
            approved_date: formData.approvedDate || null,
            next_revision_by: formData.nextRevisionBy,
            next_revision_date: formData.nextRevisionDate || null,
        };

        try {
            await dispatch(updateSanaNote({ case_id: caseId, sanaNoteData: payload })).unwrap();
            Swal.fire({
                title: "Updated!",
                text: "Progress notes updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
            // Stay on edit page
        } catch (err) {
            Swal.fire({
                title: "Error!",
                text: err || "Failed to update.",
                icon: "error",
            });
        }
    };

    // ── LOADING STATE ─────────────────────────────────────────────
    if (isLoading || !formData) {
        return (
            <MainLayout>
                <div className="text-center mt-5">Loading...</div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                {/* Top Bar: Breadcrumbs + Print Button */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/sana-notes/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        {/* Backend Error */}
                        {error && <div className="alert alert-danger">{error}</div>}

                        {/* Validation Errors */}
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((e, i) => (
                                    <div key={i}>{e}</div>
                                ))}
                            </div>
                        )}

                        {/* Form */}
                        <form onSubmit={handleSubmit}>
                            <SanaNoteForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                            />

                            {/* ==== SUBMIT ==== */}
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditSanaNote;